import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sectionGuidesApi } from '../api/sectionGuides';
import { useToast } from '../contexts/ToastContext';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  DocumentTextIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';

const SECTION_TYPES = [
  { id: 'news', label: 'News', description: 'Quick-hit announcements and updates' },
  { id: 'reads', label: 'Reads', description: 'Deeper articles and tutorials' },
  { id: 'deep-dive', label: 'Deep Dive', description: 'Opinion pieces and analysis' },
  { id: 'intro', label: 'Intro', description: 'Issue introduction and "In this issue"' },
  { id: 'multimedia', label: 'Multimedia', description: 'Podcasts and videos' },
  { id: 'community', label: 'Community', description: 'Community highlights and social' },
];

function SectionGuidesPage() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [selectedSection, setSelectedSection] = useState('news');
  const [isEditing, setIsEditing] = useState(false);
  const [showAddExample, setShowAddExample] = useState(false);
  const [newExample, setNewExample] = useState({ blurb: '', tone: 'neutral', notes: '' });

  const { data: sectionGuides, isLoading } = useQuery({
    queryKey: ['sectionGuides', 'all'],
    queryFn: sectionGuidesApi.getAll,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => sectionGuidesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['sectionGuides']);
      setIsEditing(false);
      showSuccess('Section guide updated successfully');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Failed to update section guide');
    },
  });

  const createMutation = useMutation({
    mutationFn: sectionGuidesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['sectionGuides']);
      setIsEditing(false);
      showSuccess('Section guide created successfully');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Failed to create section guide');
    },
  });

  const addExampleMutation = useMutation({
    mutationFn: ({ id, example }) => sectionGuidesApi.addExample(id, example),
    onSuccess: () => {
      queryClient.invalidateQueries(['sectionGuides']);
      setShowAddExample(false);
      setNewExample({ blurb: '', tone: 'neutral', notes: '' });
      showSuccess('Example added successfully');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Failed to add example');
    },
  });

  const removeExampleMutation = useMutation({
    mutationFn: ({ id, index }) => sectionGuidesApi.removeExample(id, index),
    onSuccess: () => {
      queryClient.invalidateQueries(['sectionGuides']);
      showSuccess('Example removed');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Failed to remove example');
    },
  });

  const currentGuide = sectionGuides?.[selectedSection];
  const sectionInfo = SECTION_TYPES.find((s) => s.id === selectedSection);

  const [formData, setFormData] = useState({
    description: '',
    wordLimits: { min: 30, max: 100 },
    rules: [],
  });

  const handleEdit = () => {
    if (currentGuide) {
      setFormData({
        description: currentGuide.description || '',
        wordLimits: currentGuide.wordLimits || { min: 30, max: 100 },
        rules: currentGuide.rules || [],
      });
    } else {
      setFormData({
        description: sectionInfo?.description || '',
        wordLimits: { min: 30, max: 100 },
        rules: [],
      });
    }
    setIsEditing(true);
  };

  const handleSave = () => {
    const data = {
      id: selectedSection,
      type: selectedSection,
      ...formData,
    };

    if (currentGuide) {
      updateMutation.mutate({ id: selectedSection, ...formData });
    } else {
      createMutation.mutate(data);
    }
  };

  const addRule = () => {
    setFormData((prev) => ({
      ...prev,
      rules: [...prev.rules, { rule: '', priority: 'recommended' }],
    }));
  };

  const updateRule = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, i) => (i === index ? { ...rule, [field]: value } : rule)),
    }));
  };

  const removeRule = (index) => {
    setFormData((prev) => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index),
    }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading section guides...</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-obn-blue dark:text-white">Section Guides</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Configure rules and examples for each newsletter section
          </p>
        </div>
      </div>

      {/* Section Selector */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {SECTION_TYPES.map((section) => {
          const hasGuide = !!sectionGuides?.[section.id];
          const isActive = selectedSection === section.id;
          return (
            <button
              key={section.id}
              onClick={() => {
                setSelectedSection(section.id);
                setIsEditing(false);
              }}
              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                isActive
                  ? 'border-obn-blue bg-obn-lightblue dark:border-blue-400 dark:bg-blue-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`font-medium ${isActive ? 'text-obn-blue dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'}`}>
                  {section.label}
                </span>
                {hasGuide && <span className="w-2 h-2 bg-green-500 rounded-full" />}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{section.description}</div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <div className="bg-white dark:bg-gray-800 shadow dark:ring-1 dark:ring-white/10 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {sectionInfo?.label} Configuration
            </h3>
            {!isEditing && (
              <button
                onClick={handleEdit}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </button>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Min Words</label>
                  <input
                    type="number"
                    value={formData.wordLimits.min}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        wordLimits: { ...formData.wordLimits, min: parseInt(e.target.value) },
                      })
                    }
                    className="mt-1 block w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Max Words</label>
                  <input
                    type="number"
                    value={formData.wordLimits.max}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        wordLimits: { ...formData.wordLimits, max: parseInt(e.target.value) },
                      })
                    }
                    className="mt-1 block w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Rules</label>
                  <button
                    type="button"
                    onClick={addRule}
                    className="text-sm text-obn-blue dark:text-blue-400 hover:text-obn-blue/80 dark:hover:text-blue-300"
                  >
                    + Add Rule
                  </button>
                </div>
                {formData.rules.map((rule, idx) => (
                  <div key={idx} className="flex items-start space-x-2 mb-2">
                    <input
                      type="text"
                      value={rule.rule}
                      onChange={(e) => updateRule(idx, 'rule', e.target.value)}
                      className="flex-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                      placeholder="Rule text"
                    />
                    <select
                      value={rule.priority}
                      onChange={(e) => updateRule(idx, 'priority', e.target.value)}
                      className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                    >
                      <option value="required">Required</option>
                      <option value="recommended">Recommended</option>
                      <option value="optional">Optional</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeRule(idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateMutation.isPending || createMutation.isPending}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90 disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </div>
          ) : currentGuide ? (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">Description</h4>
                <p className="mt-1 text-gray-600 dark:text-gray-300">{currentGuide.description}</p>
              </div>

              {currentGuide.wordLimits && (
                <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Word Limits:</span>{' '}
                  <span className="text-blue-700 dark:text-blue-300">
                    {currentGuide.wordLimits.min}-{currentGuide.wordLimits.max} words
                    {currentGuide.wordLimits.target &&
                      ` (target: ${currentGuide.wordLimits.target})`}
                  </span>
                </div>
              )}

              {currentGuide.rules?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Rules</h4>
                  <ul className="space-y-2">
                    {currentGuide.rules.map((rule, idx) => (
                      <li key={idx} className="flex items-start">
                        <span
                          className={`text-xs px-2 py-0.5 rounded mr-2 mt-0.5 ${
                            rule.priority === 'required'
                              ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200'
                              : rule.priority === 'recommended'
                                ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-200'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {rule.priority}
                        </span>
                        <span className="text-gray-600 dark:text-gray-300">{rule.rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="text-xs text-gray-400 dark:text-gray-500 pt-4 border-t border-gray-200 dark:border-gray-700">
                Last modified: {new Date(currentGuide.modified).toLocaleString()}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <DocumentTextIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No configuration for this section yet</p>
              <button
                onClick={handleEdit}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Configuration
              </button>
            </div>
          )}
        </div>

        {/* Examples Panel */}
        <div className="bg-white dark:bg-gray-800 shadow dark:ring-1 dark:ring-white/10 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Examples</h3>
            {currentGuide && (
              <button
                onClick={() => setShowAddExample(true)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Example
              </button>
            )}
          </div>

          {showAddExample && (
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-3">
              <textarea
                value={newExample.blurb}
                onChange={(e) => setNewExample({ ...newExample, blurb: e.target.value })}
                rows={3}
                className="block w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                placeholder="Example blurb text..."
              />
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={newExample.tone}
                  onChange={(e) => setNewExample({ ...newExample, tone: e.target.value })}
                  className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                >
                  <option value="agree">Agree</option>
                  <option value="disagree">Disagree</option>
                  <option value="skeptical">Skeptical</option>
                  <option value="excited">Excited</option>
                  <option value="neutral">Neutral</option>
                </select>
                <input
                  type="text"
                  value={newExample.notes}
                  onChange={(e) => setNewExample({ ...newExample, notes: e.target.value })}
                  className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  placeholder="Notes (optional)"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowAddExample(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={() => addExampleMutation.mutate({ id: selectedSection, example: newExample })}
                  disabled={!newExample.blurb.trim() || addExampleMutation.isPending}
                  className="px-3 py-1.5 text-sm bg-obn-blue text-white rounded-md hover:bg-obn-blue/90 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            </div>
          )}

          {currentGuide?.examples?.length > 0 ? (
            <ul className="space-y-4 max-h-[500px] overflow-y-auto">
              {currentGuide.examples.map((example, idx) => (
                <li key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded capitalize ${
                        example.tone === 'agree'
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-200'
                          : example.tone === 'disagree'
                            ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200'
                            : example.tone === 'skeptical'
                              ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-200'
                              : example.tone === 'excited'
                                ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {example.tone}
                    </span>
                    <button
                      onClick={() => removeExampleMutation.mutate({ id: selectedSection, index: idx })}
                      className="text-red-400 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-gray-700 dark:text-gray-200 text-sm">{example.blurb}</p>
                  {example.notes && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 italic">{example.notes}</p>
                  )}
                  {example.source && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Source: {example.source}</p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-8">
              <BookOpenIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No examples yet for this section</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SectionGuidesPage;
